import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { logger } from '@/utils/logger';
import FederationService from '@/services/federation.service';

// Define the CreateFederationDTO type locally, matching the backend type
type CreateFederationDTO = {
    name: string;
    country: string;
    region?: string | null;
    city?: string | null;
    president?: string | null;
    description?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    address?: string | null;
    logo?: string | null;
};

const isLatinOrCyrillic = (str: string) => {
    // Updated regex to properly handle Cyrillic characters, spaces, hyphens, and quotes
    return /^[a-zA-Zа-яА-ЯёЁ\s\-'"]+$/.test(str) && str.trim().length >= 2;
};

interface CreateFederationModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

export const CreateFederationModal: React.FC<CreateFederationModalProps> = ({
    visible,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values: CreateFederationDTO) => {
        try {
            setLoading(true);
            await FederationService.createFederation(values);
            message.success('Федерация успешно создана');
            form.resetFields();
            // Вызываем callback для обновления списка федераций
            onSuccess();
        } catch (error: any) {
            logger.error('Error creating federation', {
                error,
                errorMessage: error.response?.data?.message || error.message,
                formData: values
            });

            if (error.response?.status === 401) {
                message.error('Необходима авторизация');
                window.location.href = '/login';
                return;
            }

            message.error(error.message || 'Произошла ошибка при создании федерации');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Создание федерации"
            open={visible}
            onCancel={onCancel}
            onOk={form.submit}
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    region: '',
                    description: '',
                    phone: '',
                    email: '',
                    website: '',
                    address: ''
                }}
            >
                <Form.Item
                    name="name"
                    label="Название федерации"
                    rules={[
                        { required: true, message: 'Введите название федерации' },
                        { min: 2, message: 'Минимум 2 символа' },
                        { max: 100, message: 'Максимум 100 символов' },
                        { whitespace: true, message: 'Поле не может быть пустым' },
                        {
                            validator: async (_, value) => {
                                if (!value) return;

                                if (!isLatinOrCyrillic(value)) {
                                    throw new Error('Используйте только буквы, пробелы и дефис');
                                }

                                if (value.trim().length < 2) {
                                    throw new Error('Минимум 2 символа без учета пробелов');
                                }
                            }
                        }
                    ]}
                >
                    <Input placeholder="Например: Федерация карате России" />
                </Form.Item>

                <Form.Item
                    name="country"
                    label="Страна"
                    rules={[
                        { required: true, message: 'Укажите страну' },
                        { min: 2, message: 'Минимум 2 символа' },
                        { max: 100, message: 'Максимум 100 символов' },
                        { whitespace: true, message: 'Поле не может быть пустым' },
                        {
                            validator: async (_, value) => {
                                if (!value) return;

                                if (!isLatinOrCyrillic(value)) {
                                    throw new Error('Используйте только буквы, пробелы и дефис');
                                }

                                if (value.trim().length < 2) {
                                    throw new Error('Минимум 2 символа без учета пробелов');
                                }
                            }
                        }
                    ]}
                >
                    <Input placeholder="Например: Россия" />
                </Form.Item>

                <Form.Item
                    name="region"
                    label="Регион"
                    rules={[
                        { max: 100, message: 'Максимум 100 символов' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="Город"
                    rules={[
                        { max: 100, message: 'Максимум 100 символов' },
                        {
                            validator: async (_, value) => {
                                if (!value) return;

                                if (!isLatinOrCyrillic(value)) {
                                    throw new Error('Используйте только буквы, пробелы и дефис');
                                }

                                if (value.trim().length < 2) {
                                    throw new Error('Минимум 2 символа без учета пробелов');
                                }
                            }
                        }
                    ]}
                >
                    <Input placeholder="Например: Москва" />
                </Form.Item>

                <Form.Item
                    name="president"
                    label="Президент"
                    rules={[
                        { required: true, message: 'Укажите президента федерации' },
                        { max: 100, message: 'Максимум 100 символов' },
                        {
                            validator: async (_, value) => {
                                if (!value) return;

                                if (!isLatinOrCyrillic(value)) {
                                    throw new Error('Используйте только буквы, пробелы и дефис');
                                }

                                if (value.trim().length < 2) {
                                    throw new Error('Минимум 2 символа без учета пробелов');
                                }
                            }
                        }
                    ]}
                >
                    <Input placeholder="Например: Иванов Иван Иванович" />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                    rules={[
                        { max: 1000, message: 'Максимум 1000 символов' }
                    ]}
                >
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Телефон"
                    rules={[
                        { pattern: /^\+?[\d\s-()]+$/, message: 'Введите корректный номер телефона' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { type: 'email', message: 'Введите корректный email' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="website"
                    label="Веб-сайт"
                    rules={[
                        { type: 'url', message: 'Введите корректный URL' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Адрес"
                    rules={[
                        { max: 200, message: 'Максимум 200 символов' }
                    ]}
                >
                    <Input />
                </Form.Item>

                {/* Removed logo Form.Item */}
            </Form>
        </Modal>
    );
};
